import clsx from "clsx";
import TwoSidebars from "../../layouts/TwoSidebars/TwoSidebars";
import { useNavigate, useParams } from "react-router-dom";

import PageTitle from "../../components/PageTitle/PageTitle";
import ButtonGroup from "../../components/ButtonGroup/ButtonGroup";
import { MdOutlineNavigateBefore, MdSettings } from "react-icons/md";
import { useEffect, useMemo } from "react";
import CategoryEditPanel from "../../components/CategoryEditPanel/CategoryEditPanel";
import LoadingWithText from "../../components/LoadingWithText/LoadingWithText";
import { useTranslation } from "react-i18next";
import SettingsBlocks from "../../components/SettingsBlocks/SettingsBlocks";
import { BUTTON_STYLES, ICON_SIZES } from "../../helpers/constants";
import useMobile from "../../hooks/useMobile";

import useGeneralOptionsDropdownMenu from "../../hooks/useGeneralOptionsDropdownMenu";
import useAllTransactions from "src/hooks/useAllTransactions";
import { AnimatePresence } from "framer-motion";

const CategoryPage: React.FC = () => {
  const generalOptionsDropdown = useGeneralOptionsDropdownMenu();
  const {
    isLoadingAllTransactions,
    getCategoryCustomSettings,
    isLoadingCustomizedCategories,
    categoryExists,
  } = useAllTransactions();

  const { isMobile } = useMobile();
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { category: categoryParam } = useParams();

  const isLoadingSomething = useMemo(
    () =>
      [!!isLoadingAllTransactions, !!isLoadingCustomizedCategories]?.includes(
        true
      ),
    [isLoadingAllTransactions, isLoadingCustomizedCategories]
  );

  useEffect(() => {
    if (!isLoadingSomething) {
      if (!categoryExists(categoryParam as string)) {
        navigate("/404", { replace: true });
      }
    }
  }, [isLoadingSomething, categoryParam]);

  const categoryData = useMemo(() => {
    return {
      category: categoryParam,
      settings: getCategoryCustomSettings(categoryParam as string),
    };
  }, [categoryParam, getCategoryCustomSettings]);

  return (
    <TwoSidebars
      right={
        isLoadingSomething ? null : isMobile ? null : (
          <SettingsBlocks layout="vertical" columns={isMobile ? 2 : 1} />
        )
      }
      isLoading={isLoadingSomething}
    >
      <div
        className={clsx([
          "flex",
          "flex-col",
          "w-full",
          "gap-2",
          "md:gap-2",
          "h-full",
        ])}
      >
        <AnimatePresence>
          {isLoadingSomething ? (
            <div
              className={clsx([
                "flex",
                "justify-center",
                "w-full",
                "items-center",
                "flex-grow",
                "md:mt-0",
              ])}
            >
              <LoadingWithText />
            </div>
          ) : (
            <>
              <PageTitle
                icon={<MdSettings size={ICON_SIZES.MD} />}
                title={t("settings.title")}
                titleLink="/settings"
                subtitle={[
                  {
                    text: "Categorías",
                    link: "/settings/categories",
                  },
                  {
                    text: "Editar",
                  },
                ]}
                isLoading={false}
                rightSide={
                  <>
                    <ButtonGroup
                      align="right"
                      gap={1}
                      buttons={[
                        {
                          icon: (
                            <MdOutlineNavigateBefore size={ICON_SIZES.SM} />
                          ),
                          className: isMobile ? BUTTON_STYLES.HEADING : "",

                          tooltip: "Atrás",
                          onClick: () => {
                            navigate("/settings/categories");
                          },
                        },
                        generalOptionsDropdown,
                      ]}
                    />
                  </>
                }
              />

              <CategoryEditPanel category={categoryData} key={categoryParam} />
            </>
          )}
        </AnimatePresence>
      </div>
    </TwoSidebars>
  );
};

export default CategoryPage;
